import React, { useState } from 'react';
import http from '../api/http';
import { useToasts } from '../hooks/useToasts';

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  const overlayStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1050,
  };

  const contentStyle = {
    position: 'relative',
    backgroundColor: 'white',
    padding: '2rem',
    borderRadius: '8px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflowY: 'auto',
    boxShadow: '0 5px 15px rgba(0, 0, 0, 0.3)',
  };

  const closeButtonStyle = {
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    background: 'transparent',
    border: 'none',
    fontSize: '1.5rem',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
  };

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={contentStyle} onClick={e => e.stopPropagation()}>
        <button style={closeButtonStyle} onClick={onClose}>&times;</button>
        {children}
      </div>
    </div>
  );
};

const AddEmployeeForm = ({ onAdd, onClose }) => {
  const { showSuccess, showError } = useToasts();
  const [formData, setFormData] = useState({
    name: '',
    department: '',
    section: '',
    team: '',
    position: '',
    id_card_number: '',
    gender: '男',
    health_status: '',
    hometown: '',
    household_registration: '',
    address: '',
    phone: '',
    marital_status: '未婚',
    spouse_name: '',
    is_dual_employee: false,
    emergency_contact_name: '',
    emergency_contact_relation: '',
    emergency_contact_phone: '',
    ethnicity: '',
    birth_date: '',
    political_status: '',
    education: '',
    hire_date: '',
    contract_end_date: '',
    certification_status: '',
    meal_card_number: '',
    on_the_job_medical_exam_date: '',
    email: ''
  });
  const [errors, setErrors] = useState({});

  // 根据身份证号码计算年龄和出生日期
  const calculateAgeFromIdCard = (idCardNumber) => {
    if (!idCardNumber || idCardNumber.length < 17) return { age: null, birthDate: null };
    
    let birthYear, birthMonth, birthDay;
    // 处理18位身份证
    if (idCardNumber.length === 18) {
      birthYear = idCardNumber.substring(6, 10);
      birthMonth = idCardNumber.substring(10, 12);
      birthDay = idCardNumber.substring(12, 14);
    } 
    // 处理15位身份证
    else if (idCardNumber.length === 15) {
      birthYear = '19' + idCardNumber.substring(6, 8);
      birthMonth = idCardNumber.substring(8, 10);
      birthDay = idCardNumber.substring(10, 12);
    }
    
    if (birthYear && birthMonth && birthDay) {
      const birthDate = new Date(birthYear, birthMonth - 1, birthDay);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      
      // 调整年龄计算，考虑生日是否已过
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
      
      // 格式化出生日期为YYYY-MM-DD
      const formattedBirthDate = `${birthYear}-${birthMonth.padStart(2, '0')}-${birthDay.padStart(2, '0')}`;
      return { age, birthDate: formattedBirthDate };
    }
    
    return { age: null, birthDate: null };
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = '姓名不能为空';
    if (!formData.id_card_number) newErrors.id_card_number = '身份证号码不能为空';
    
    if (!formData.phone) {
      newErrors.phone = '联系方式不能为空';
    } else if (!/^\d{11}$/.test(formData.phone)) {
      newErrors.phone = '请输入有效的11位手机号码';
    }

    // 邮箱设为可空，但如果填写了则进行格式验证
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = '请输入有效的邮箱地址';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    // 如果是身份证号码变更，自动计算年龄和出生日期
    if (name === 'id_card_number') {
      const { age, birthDate } = calculateAgeFromIdCard(value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        age: age,
        birth_date: birthDate || prev.birth_date
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      return;
    }
    try {
  // 修正：后端其余获取员工列表使用 '/employees'，此处与之保持一致避免 404
  const resp = await http.post('/employees', formData);
  showSuccess('员工添加成功！');
  onAdd(resp.data);
      onClose();
    } catch (err) {
  const serverMsg = err?.response?.data?.error || err.friendlyMessage || err.message;
  showError(serverMsg || '添加失败，请稍后重试');
  setErrors({ submit: serverMsg });
    }
  };

  const formRowStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1rem',
  };

  const formGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
  };

  const labelStyle = {
    marginBottom: '0.5rem',
    color: 'var(--text-secondary)',
    fontSize: '14px',
  };

  const inputStyle = {
    padding: '0.75rem',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '14px',
  };
  
  const buttonContainerStyle = {
      display: 'flex',
      justifyContent: 'flex-end',
      marginTop: '2rem',
      borderTop: '1px solid var(--border-color)',
      paddingTop: '1rem'
  };

  const submitButtonStyle = {
    padding: '10px 20px',
    border: 'none',
    borderRadius: '6px',
    backgroundColor: 'var(--primary-color)',
    color: 'white',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
  };

  const errorStyle = {
    color: '#ef4444',
    fontSize: '12px',
    marginTop: '4px',
  };

  return (
    <form onSubmit={handleSubmit}>
      <h3 style={{ color: 'var(--text-primary)', marginBottom: '2rem' }}>人员资料登记</h3>
      
      <div style={formRowStyle}>
        <div style={formGroupStyle}>
          <label style={labelStyle}>姓名*</label>
          <input style={inputStyle} type="text" name="name" value={formData.name} onChange={handleChange} />
          {errors.name && <p style={errorStyle}>{errors.name}</p>}
        </div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>身份证号码*</label>
          <input style={inputStyle} type="text" name="id_card_number" value={formData.id_card_number} onChange={handleChange} />
          {errors.id_card_number && <p style={errorStyle}>{errors.id_card_number}</p>}
        </div>
        <div style={formGroupStyle}><label style={labelStyle}>性别</label><select style={inputStyle} name="gender" value={formData.gender} onChange={handleChange}><option>男</option><option>女</option></select></div>
        <div style={formGroupStyle}><label style={labelStyle}>民族</label><input style={inputStyle} type="text" name="ethnicity" value={formData.ethnicity} onChange={handleChange} /></div>
      </div>

      <div style={formRowStyle}>
        <div style={formGroupStyle}><label style={labelStyle}>工作部门</label><input style={inputStyle} type="text" name="department" value={formData.department} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>科室(工序)</label><input style={inputStyle} type="text" name="section" value={formData.section} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>班组</label><input style={inputStyle} type="text" name="team" value={formData.team} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>工作岗位</label><input style={inputStyle} type="text" name="position" value={formData.position} onChange={handleChange} /></div>
      </div>
      
      <div style={formRowStyle}>
        <div style={formGroupStyle}><label style={labelStyle}>出生年月</label><input style={inputStyle} type="date" name="birth_date" value={formData.birth_date} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>政治面貌</label><input style={inputStyle} type="text" name="political_status" value={formData.political_status} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>学历</label><input style={inputStyle} type="text" name="education" value={formData.education} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>婚姻状况</label><select style={inputStyle} name="marital_status" value={formData.marital_status} onChange={handleChange}><option>未婚</option><option>已婚</option><option>离异</option><option>丧偶</option></select></div>
      </div>

      <div style={formRowStyle}>
        <div style={formGroupStyle}><label style={labelStyle}>籍贯</label><input style={inputStyle} type="text" name="hometown" value={formData.hometown} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>户口所在地</label><input style={inputStyle} type="text" name="household_registration" value={formData.household_registration} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>家庭住址</label><input style={inputStyle} type="text" name="address" value={formData.address} onChange={handleChange} /></div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>联系方式*</label>
          <input style={inputStyle} type="text" name="phone" value={formData.phone} onChange={handleChange} />
          {errors.phone && <p style={errorStyle}>{errors.phone}</p>}
        </div>
      </div>

      <div style={formRowStyle}>
        <div style={formGroupStyle}><label style={labelStyle}>配偶姓名</label><input style={inputStyle} type="text" name="spouse_name" value={formData.spouse_name} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>是否双职工</label><input type="checkbox" name="is_dual_employee" checked={formData.is_dual_employee} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>紧急联系人</label><input style={inputStyle} type="text" name="emergency_contact_name" value={formData.emergency_contact_name} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>紧急联系人称谓</label><input style={inputStyle} type="text" name="emergency_contact_relation" value={formData.emergency_contact_relation} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>紧急联系人号码</label><input style={inputStyle} type="text" name="emergency_contact_phone" value={formData.emergency_contact_phone} onChange={handleChange} /></div>
      </div>

      <div style={formRowStyle}>
        <div style={formGroupStyle}><label style={labelStyle}>进入公司时间</label><input style={inputStyle} type="date" name="hire_date" value={formData.hire_date} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>最新合同期限</label><input style={inputStyle} type="date" name="contract_end_date" value={formData.contract_end_date} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>持证情况</label><input style={inputStyle} type="text" name="certification_status" value={formData.certification_status} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>饭卡号</label><input style={inputStyle} type="text" name="meal_card_number" value={formData.meal_card_number} onChange={handleChange} /></div>
      </div>

      <div style={formRowStyle}>
        <div style={formGroupStyle}><label style={labelStyle}>职业健康体检情况</label><input style={inputStyle} type="text" name="health_status" value={formData.health_status} onChange={handleChange} /></div>
        <div style={formGroupStyle}><label style={labelStyle}>岗中体检日期</label><input style={inputStyle} type="date" name="on_the_job_medical_exam_date" value={formData.on_the_job_medical_exam_date} onChange={handleChange} /></div>
        <div style={formGroupStyle}>
          <label style={labelStyle}>邮箱*</label>
          <input style={inputStyle} type="email" name="email" value={formData.email} onChange={handleChange} />
          {errors.email && <p style={errorStyle}>{errors.email}</p>}
        </div>
      </div>

      {errors.submit && <p style={{ color: '#ef4444', marginTop: '1rem' }}>{errors.submit}</p>}

      <div style={buttonContainerStyle}>
        <button type="submit" style={submitButtonStyle}>提交</button>
      </div>
    </form>
  );
};


const AddEmployeeModal = ({ isOpen, onClose, onAdd }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <AddEmployeeForm onAdd={onAdd} onClose={onClose} />
    </Modal>
  );
};

export default AddEmployeeModal;
